import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        // Show success toast
        toast({
          title: "✅ Message envoyé avec succès !",
          description: "Merci pour votre message. Notre équipe vous répondra dans les 24-48 heures.",
          duration: 8000,
        });
        
        // Show alert message
        alert(
          "✅ MESSAGE ENVOYÉ AVEC SUCCÈS !\n\n" +
          "Merci de nous avoir contactés.\n\n" +
          "Votre demande d'information a été reçue et notre équipe vous répondra dans les 24 à 48 heures.\n\n" +
          "Un membre de l'équipe PROTAXESPLUS communiquera avec vous prochainement."
        );
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "❌ Erreur",
        description: "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer ou nous contacter directement par téléphone au (514) 660-4711.",
        variant: "destructive",
        duration: 10000,
      });
      
      // Show error alert
      alert(
        "❌ ERREUR D'ENVOI\n\n" +
        "Une erreur est survenue lors de l'envoi de votre message.\n\n" +
        "Veuillez réessayer ou nous contacter directement :\n" +
        "Téléphone : (514) 660-4711\n" +
        "Email : infos@protaxesplus.ca"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            <span className="text-blue-600">Contactez</span>-nous
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nous sommes là pour répondre à toutes vos questions et vous accompagner dans vos besoins fiscaux
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Coordonnées
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-blue-50 rounded-xl border-2 border-blue-200 hover:border-blue-600 transition-colors duration-200 cursor-pointer">
                  <div className="bg-blue-600 rounded-lg p-3 flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Courriel</h3>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className="text-blue-600 hover:underline block"
                      title="Cliquez pour envoyer un email"
                    >
                      {companyInfo.email}
                      <br />
                      <span className="text-sm text-blue-500 mt-1 inline-flex items-center">
                        ✉️ Envoyer un email
                      </span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-green-50 rounded-xl border-2 border-green-200 hover:border-green-600 transition-colors duration-200 cursor-pointer">
                  <div className="bg-green-600 rounded-lg p-3 flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Téléphone</h3>
                    <a 
                      href={`tel:${companyInfo.phone}`}
                      className="text-green-600 hover:underline block"
                      title="Cliquez pour appeler"
                    >
                      {companyInfo.phone}
                      <br />
                      <span className="text-sm text-green-500 mt-1 inline-flex items-center">
                        📞 Appeler maintenant
                      </span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-blue-50 rounded-xl border-2 border-blue-200 hover:border-blue-600 transition-colors duration-200 cursor-pointer">
                  <div className="bg-blue-600 rounded-lg p-3 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Adresse</h3>
                    <a 
                      href="https://www.google.com/maps/dir/?api=1&destination=6500+Route+Transcanadienne,+Saint-Laurent,+QC+H4T+1X4"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline block"
                      title="Cliquez pour obtenir l'itinéraire sur Google Maps"
                    >
                      {companyInfo.address}
                      <br />
                      {companyInfo.suite}
                      <br />
                      <span className="text-sm text-blue-500 mt-1 inline-flex items-center">
                        📍 Obtenir l'itinéraire
                      </span>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-green-50 rounded-xl border-2 border-green-200 hover:border-green-600 transition-colors duration-200">
                  <div className="bg-green-600 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Heures d'ouverture</h3>
                    <p className="text-gray-700">{companyInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Envoyez-nous un message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 focus:outline-none transition-colors duration-200"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Courriel *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 focus:outline-none transition-colors duration-200"
                      placeholder="votre.email@exemple.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 focus:outline-none transition-colors duration-200"
                      placeholder="(514) 555-0123"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 focus:outline-none transition-colors duration-200"
                      placeholder="Sujet de votre message"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 focus:outline-none transition-colors duration-200 resize-none"
                      placeholder="Comment pouvons-nous vous aider?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}</span>
                    <Send className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Notre emplacement
          </h2>
          <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-blue-200">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d46531.692343377115!2d-73.73524893100932!3d45.46237296373273!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc917e030063cff%3A0x63ce420cf7239a53!2sPRO%20TAXES%20PLUS!5e0!3m2!1sfr!2sca!4v1768877349665!5m2!1sfr!2sca" 
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Carte de localisation PROTAXESPLUS"
            ></iframe>
          </div>
          <a 
            href="https://www.google.com/maps/dir/?api=1&destination=6500+Route+Transcanadienne,+Saint-Laurent,+QC+H4T+1X4"
            target="_blank"
            rel="noopener noreferrer"
            className="text-center text-gray-700 mt-6 block hover:text-blue-600 transition-colors duration-200"
            title="Cliquez pour obtenir l'itinéraire sur Google Maps"
          >
            <strong className="text-blue-900">PROTAXESPLUS</strong><br />
            6500 route Transcanadienne, Saint-Laurent, H4T 1X4<br />
            (Suite 120-D)<br />
            <span className="text-blue-600 font-semibold mt-2 inline-block">
              📍 Cliquez ici pour obtenir l'itinéraire
            </span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
